import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const aboutSchema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: "关于新御宅屋",
  description:
    "新御宅屋是一个动漫同好社区，提供求番推荐、私藏安利、漫评讨论与避雷指南，以及按顺序补完老番的补番攻略。",
};

const values = [
  {
    icon: "ri-heart-3-line",
    title: "热爱为先",
    desc: "我们相信每一份对动漫的热爱都值得被认真对待，无论你是十年老宅还是刚刚入坑的新人。",
    tone: "bg-primary-100 text-primary-700",
  },
  {
    icon: "ri-focus-3-line",
    title: "内容为真",
    desc: "坚持真实体验与客观评价，避雷不夸大、安利不注水，让每一条推荐都经得起同好的检验。",
    tone: "bg-accent-100 text-accent-700",
  },
  {
    icon: "ri-team-line",
    title: "同好为本",
    desc: "社区因同好而存在，鼓励分享与交流，让「不知道看什么」的难题在大家的热心回答里迎刃而解。",
    tone: "bg-secondary-100 text-secondary-700",
  },
  {
    icon: "ri-time-line",
    title: "效率至上",
    desc: "帮你把有限的时间花在真正值得的番上，从选番、避雷到补番顺序，一站式省心到底。",
    tone: "bg-primary-100 text-primary-700",
  },
];

const principles = [
  "避雷指南从节奏、作画、剧情、烂尾风险多角度综合整理，兼顾口碑与真实体验。",
  "求番推荐围绕同好真实偏好给答案，不盲目吹捧热门、不跟风踩冷门。",
  "补番顺序梳理连载顺序与剧情时间线，避免剧透和顺序错乱。",
  "漫评讨论欢迎不同观点，理性交流、互相尊重是同好畅聊的前提。",
  "私藏安利由同好投稿，经社区口碑筛选后发布，优质内容会被推荐到首页精选。",
];

export default function About() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />
      <Navbar />
      <main>
        {/* 页头 */}
        <section className="bg-gradient-to-br from-primary-950 to-primary-800 pt-24 md:pt-28">
          <div className="container-x relative z-10 py-12 md:py-16">
            <nav aria-label="面包屑" className="mb-5 flex items-center gap-2 text-xs text-background-50/80">
              <Link to="/" className="transition-colors hover:text-background-50">
                <i className="ri-home-4-line mr-1"></i>首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-background-50/95">关于我们</span>
            </nav>
            <h1 className="font-heading text-3xl text-background-50 md:text-4xl">关于新御宅屋</h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-background-50/85 md:text-base">
              一个属于动漫同好的社区，愿陪你把每一部好番都追完。
            </p>
          </div>
        </section>

        {/* 品牌定位 */}
        <section className="container-x py-14 md:py-16">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-primary-700">品牌定位</span>
              <h2 className="mt-2 font-heading text-2xl text-foreground-900 md:text-3xl">我们是谁，我们为什么存在</h2>
              <p className="mt-4 text-sm leading-7 text-foreground-600 md:text-base">
                新御宅屋诞生于一个朴素的愿望：让「下一部看什么」不再是一道难题。过去，选番全靠封面和运气，
                踩过太多坑、错过太多好番；于是我们把同好聚到一起，把求番推荐、私藏安利、避雷指南和补番顺序整理成一份随时可查的「看番地图」。
              </p>
              <p className="mt-3 text-sm leading-7 text-foreground-600 md:text-base">
                今天的新御宅屋，是求番时的热心回答、是避雷时的真心提醒、是补番时的清晰路线，更是每一位同好分享热爱的地方。
              </p>
              <div className="mt-6 grid grid-cols-3 gap-4">
                {[
                  { v: "4", l: "核心栏目" },
                  { v: "200+", l: "原创内容" },
                  { v: "1.8w+", l: "同好成员" },
                ].map((s) => (
                  <div key={s.l} className="card p-4 text-center">
                    <div className="font-latin text-2xl font-extrabold text-primary-700">{s.v}</div>
                    <div className="mt-1 text-xs text-foreground-500">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="card overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Vibrant%20anime%20illustration%20of%20a%20friendly%20community%20of%20anime%20fans%20gathering%20around%20a%20large%20shared%20screen%20and%20manga%20table%2C%20warm%20coral%20orange%20and%20amber%20palette%2C%20soft%20cream%20background%2C%20flat%20vector%20art%2C%20cozy%20welcoming%20composition%2C%20modern%20anime%20community%20style&width=800&height=700&seq=about-community-1&orientation=portrait"
                alt="新御宅屋同好社区场景插画"
                title="新御宅屋同好交流社区"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* 理念 */}
        <section className="bg-background-100">
          <div className="container-x py-14 md:py-16">
            <div className="mb-8 max-w-2xl">
              <span className="text-xs font-bold uppercase tracking-widest text-accent-700">发展理念</span>
              <h2 className="mt-2 font-heading text-2xl text-foreground-900 md:text-3xl">我们坚持的四件事</h2>
              <p className="mt-2 text-sm leading-6 text-foreground-600">指引新御宅屋一路走来的信念，也是我们对每一位同好的承诺。</p>
            </div>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {values.map((v) => (
                <div key={v.title} className="card p-6">
                  <span className={`flex h-11 w-11 items-center justify-center rounded-xl text-xl ${v.tone}`}>
                    <i className={v.icon}></i>
                  </span>
                  <h3 className="mt-4 font-bold text-foreground-900">{v.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-foreground-600">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 内容原则 */}
        <section className="container-x py-14 md:py-16">
          <div className="card grid grid-cols-1 items-center gap-8 p-6 md:p-10 lg:grid-cols-2">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-secondary-700">内容原则</span>
              <h2 className="mt-2 font-heading text-2xl text-foreground-900 md:text-3xl">我们怎样做内容</h2>
              <p className="mt-3 text-sm leading-6 text-foreground-600">
                好内容值得认真对待。我们用一套清晰的原则来保证每一条推荐、每一篇避雷和每一份攻略都靠谱、可信、有参考价值。
              </p>
              <Link to="/contact" className="btn-primary mt-6">
                投稿 / 建议
              </Link>
            </div>
            <ul className="space-y-3">
              {principles.map((p, i) => (
                <li key={p} className="flex items-start gap-3 rounded-xl bg-background-100 p-4">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-secondary-500 text-xs font-bold text-background-50">
                    {i + 1}
                  </span>
                  <span className="text-sm leading-6 text-foreground-700">{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* 联系 CTA */}
        <section className="container-x pb-14 md:pb-16">
          <div className="card border-primary-200 bg-primary-50 p-8 text-center md:p-12">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary-500 text-2xl text-background-50">
              <i className="ri-message-3-line"></i>
            </span>
            <h2 className="mt-4 font-heading text-2xl text-foreground-900 md:text-3xl">加入新御宅屋，和同好一起追番</h2>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-foreground-600">
              想投稿安利、提出建议，或只是想找个人聊聊刚看完的那部番？欢迎联系我们，我们随时都在。
            </p>
            <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
              <Link to="/contact" className="btn-primary">
                联系我们
              </Link>
              <Link to="/recommend" className="btn-outline">
                去求番推荐
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}